import {
  CatalogModifier,
  CatalogItemVariation,
  Location,
  ComponentTimePeriods,
  CatalogItem,
  CatalogTax,
  Enum_Catalogtax_Inclusion_Type,
  ComponentOrderLineItemInput,
  ComponentOrderLineItem,
  Maybe,
} from "./queries/auto-generated/schemas";
import { BuiltItem, TodaysHours, ModificationSelections } from "./types";
import _ from "lodash";
import moment, { Moment } from "moment-timezone";

export const prettyUrlFragment = (input: string): string => {
  const separator = "-";
  return input
    .replace(/\s/g, separator)
    .split(separator)
    .map(encodeURIComponent)
    .join(separator);
};

export const prettyMoney = (
  amount: number,
  currency: string = DEFAULT_CURRENCY
) => {
  if (currency === DEFAULT_CURRENCY) {
    const dollars = (amount / 100).toFixed(2);
    return `$${dollars}`;
  }

  return `${amount} ${currency}`;
};

export const DEFAULT_CURRENCY = "USD";

export const calculatePrice = (
  modifiers: { [key: string]: CatalogModifier },
  variations: { [key: string]: CatalogItemVariation },
  choices?: BuiltItem
): number => {
  let price = 0;

  if (!choices) {
    return price;
  }

  const chosenVariation = _.find(
    variations,
    (v) => v.id === choices?.variation_id
  );

  if (chosenVariation) {
    price += chosenVariation.price_money?.amount ?? 0;
  }

  const selectedModifiers = _.flatten(
    _.map(choices.modifications, (modInfo) =>
      modInfo.selected.map((square_id) =>
        _.find(modifiers, (m) => m.square_id === square_id)
      )
    )
  );

  price += selectedModifiers
    .map((mod) => mod?.price_money?.amount ?? 0)
    .reduce((accumulator, current) => accumulator + current, 0);

  price *= choices.quantity;

  return price;
};

export const calculateSubtotal = (
  modifiers: { [key: string]: CatalogModifier },
  variations: { [key: string]: CatalogItemVariation },
  lineItems: BuiltItem[]
): number => {
  let subtotal = 0;

  subtotal += lineItems
    .map((lineItem) => calculatePrice(modifiers, variations, lineItem))
    .reduce((accumulator, current) => accumulator + current, 0);

  return subtotal;
};

export const calculateTotalTaxes = (
  lineItems: BuiltItem[],
  items: { [key: string]: CatalogItem },
  taxes: { [key: string]: CatalogTax },
  modifiers: { [key: string]: CatalogModifier },
  variations: { [key: string]: CatalogItemVariation }
): number => {
  let totalTaxes = 0;

  const taxesForEachItem = lineItems.map((lineItem) => {
    const item = items[lineItem.item_id];
    const percentage = getTaxPercentage(item, taxes);
    const priceBeforeTax = calculatePrice(modifiers, variations, lineItem);
    return priceBeforeTax * percentage;
  });

  totalTaxes = taxesForEachItem.reduce(
    (accumulator, current) => accumulator + current,
    0
  );

  // Round taxes up to the nearest cent
  return Math.ceil(totalTaxes * 100) / 100;
};

export const getTaxPercentage = (
  item: CatalogItem,
  taxes: { [key: string]: CatalogTax }
): number => {
  let percentage = 0;

  try {
    const foundTaxes = (
      item?.taxes?.map((tax) => taxes[tax?.id ?? ""]) ?? []
    ).filter((tax) => tax !== undefined);

    const activeTaxes = foundTaxes.filter(
      (tax) =>
        tax.enabled &&
        tax.inclusion_type === Enum_Catalogtax_Inclusion_Type.Additive
    );

    percentage =
      activeTaxes
        .map((tax) => Number(tax?.percentage ?? 0))
        .reduce((accumulator, current) => accumulator + current, 0) / 100;
  } catch (e) {
    console.error(e);
  }

  return percentage;
};

export const DATE_INPUT_FORMAT = "YYYY-MM-DD";
export const TIME_INPUT_FORMAT = "hh:mm:ss";
export const INPUT_FORMAT = `${DATE_INPUT_FORMAT} ${TIME_INPUT_FORMAT}`;
export const OUTPUT_FORMAT = "h:mm A";
export const RFC339_FORMAT = "YYYY-MM-DDTHH:mm:ss.SSS[Z]";

export const getTodaysHours = (location: Location): TodaysHours | null => {
  if (
    location.business_hours &&
    (location?.business_hours?.periods ?? []).length === 0
  ) {
    return null;
  }

  const periods = (location.business_hours?.periods ?? []).filter(
    (period) => period !== null && period !== undefined
  ) as ComponentTimePeriods[];

  const timeZoneString = location?.timezone ?? "UTC";

  const locationTimeZoneMoment = moment.tz(timeZoneString);
  const dayOfWeek = locationTimeZoneMoment.format("ddd");
  const shortTimeZone = locationTimeZoneMoment.format("z");
  const locationDateString = locationTimeZoneMoment.format("YYYY-MM-DD");

  let open = false;

  const foundPeriods = periods.filter(
    (p) => p.day_of_week === dayOfWeek.toUpperCase()
  );

  foundPeriods.forEach((period) => {
    const now = moment.tz();
    const start = moment.tz(
      `${locationDateString} ${period.start_local_time}`,
      INPUT_FORMAT,
      timeZoneString
    );
    const end = moment.tz(
      `${locationDateString} ${period.end_local_time}`,
      INPUT_FORMAT,
      timeZoneString
    );

    // Only update the open status if open is false
    if (!open) {
      open = now.isBetween(start, end);
    }
  });

  return {
    periods: foundPeriods,
    open,
    dayOfWeek,
    shortTimeZone,
  };
};

export const readEnv = (key: string): string => {
  const value = process.env?.[key] ?? "";
  return value.trim();
};

export const builtItemsToLineItems = (
  builtItems: BuiltItem[],
  variations: { [key: string]: CatalogItemVariation }
): ComponentOrderLineItemInput[] => {
  const lineItems: ComponentOrderLineItemInput[] = builtItems.map(
    (builtItem) => {
      const modifiers = _.flatten(
        _.map(builtItem.modifications, (mod) =>
          mod.selected.map((square_id) => ({ catalog_object_id: square_id }))
        )
      );
      const variation_square_id = variations[builtItem.variation_id]?.square_id;
      return {
        quantity: String(builtItem.quantity),
        catalog_object_id: variation_square_id,
        modifiers,
        note: builtItem.note,
      };
    }
  );

  return lineItems;
};

export const lineItemsToBuiltItems = (
  squareLineItems: Maybe<ComponentOrderLineItem>[] | null | undefined,
  variations: { [key: string]: CatalogItemVariation },
  items: { [key: string]: CatalogItem },
  modifiers: { [key: string]: CatalogModifier }
) => {
  let missingMods = 0;
  let missingItems = 0;
  let builtItems: BuiltItem[] = [];

  squareLineItems?.forEach((squareLineItem) => {
    const variation = _.find(
      variations,
      (variation) => variation.square_id === squareLineItem?.catalog_object_id
    );

    if (variation === undefined) {
      missingItems++;
      return;
    }

    const squareItemId = variation.item_id;

    const item = _.find(items, (item) => item.square_id === squareItemId);

    if (item === undefined) {
      missingItems++;
      return;
    }

    const jooModifiers = squareLineItem?.modifiers?.map((squareMod) =>
      _.find(
        modifiers,
        (modifier) => modifier.square_id === squareMod?.catalog_object_id
      )
    );

    const modifications: ModificationSelections = {};

    jooModifiers?.forEach((jooModifier) => {
      if (!jooModifier) {
        missingMods++;
        return;
      }

      const modListInfo = item.modifier_list_info?.find(
        (listInfo) =>
          listInfo?.modifier_list_id === jooModifier?.modifier_list_id
      );

      if (!modListInfo) {
        missingMods++;
        return;
      }

      if (modifications[modListInfo?.id]) {
        modifications[modListInfo?.id].selected.push(jooModifier?.square_id);
      } else {
        modifications[modListInfo?.id] = {
          modifier_list_info_id: modListInfo.id,
          selected: [jooModifier.square_id],
        };
      }
    });

    builtItems.push({
      item_id: item.id,
      modifications,
      variation_id: variation.id,
      quantity: Number(squareLineItem?.quantity),
      note: squareLineItem?.note ?? undefined,
    });
  });

  return { builtItems, missingMods, missingItems };
};

export const calculatePrettyTotals = (
  totalCents: number | null | undefined,
  totalTaxCents: number | null | undefined,
  tipCents: number | null | undefined
) => {
  const prettyTotal = totalCents ? prettyMoney(totalCents) : null;
  const prettyTaxes = totalTaxCents ? prettyMoney(totalTaxCents) : null;
  const prettyTip = tipCents ? prettyMoney(tipCents) : null;
  const prettySubtotal =
    totalCents && totalTaxCents
      ? prettyMoney(totalCents - totalTaxCents - (tipCents || 0))
      : null;

  return { prettyTotal, prettySubtotal, prettyTaxes, prettyTip };
};

export const ASAP = "ASAP";

export const convertMomentToRFC3339TimeStamp = (input: Moment) => {
  return input.utc().format(RFC339_FORMAT);
};
    
export const isItemAvailableAtLocation = (catalogItem: CatalogItem, selectedLocationSquareId?: string) => {
  if (!selectedLocationSquareId) {
    return false
  }

  // Square made the odd choice of allowing an item to be both present at all locations and absent at one ore more locations at the same time.
  // Thus, the absent locations must be considered here.
  const absent_at_location_ids = isStringArray(catalogItem?.meta?.absent_at_location_ids) ? (catalogItem?.meta?.absent_at_location_ids || []) : [];
  for (const locationSquareId of absent_at_location_ids) {
    if (locationSquareId === selectedLocationSquareId) {
      return false;
    }
  }

  if (catalogItem?.meta?.present_at_all_locations === true) {
    return true;
  }

  const presentLocationSquareIds = isStringArray(catalogItem?.meta?.present_at_location_ids) ? (catalogItem?.meta?.present_at_location_ids || []) : [];
  for (const locationSquareId of presentLocationSquareIds) {
    if (locationSquareId === selectedLocationSquareId) {
      return true;
    }
  }

  return false;
};

function isStringArray(input: any): input is string[] {
  if (!Array.isArray(input)) {
    return false
  }
  for (let x of input) {
    if (typeof x !== "string") {
      return false
    }
  }
  return true
}